import { useQuery } from '@tanstack/react-query';

import { nguoiDungRepository } from '../../../lib/repositories/nguoi-dung.repository';
import { nhanSuNenTangRepository } from '../../../lib/repositories/nhan-su-nen-tang.repository';
import { nhatKyRepository } from '../../../lib/repositories/nhat-ky.repository';
import type { NguoiDungRutGon } from '../types';

/** Trang tối đa của danh sách người dùng cổng tenant: 5 × 100 người, đủ cho quy mô vài trăm người (ADR-0013). */
const SO_TRANG_TENANT = 5;
const MOT_TRANG = 100;
/** Số người mỗi lần tìm ở cổng nền tảng; tìm theo tên ở server nên không cần tải hết. */
const MOT_LAN_TIM = 50;

async function tatCaNguoiDungTenant(): Promise<NguoiDungRutGon[]> {
  const ra: NguoiDungRutGon[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < SO_TRANG_TENANT; i += 1) {
    const trang = await nguoiDungRepository.danhSach({ limit: MOT_TRANG, cursor });
    ra.push(...trang.items.map((u) => ({ user_id: u.user_id, display_name: u.display_name, email: u.email })));
    if (!trang.next_cursor) break;
    cursor = trang.next_cursor;
  }
  return ra;
}

/**
 * Người dùng cho ô lọc "Người dùng" của nhật ký.
 *
 * - Cổng tenant: `GET /api/v1/users` của chính tenant trong token, tải một lần
 *   rồi lọc tại chỗ.
 * - Cổng nền tảng: `GET /api/v1/tenants/{id}/users?search=` (tìm ở server) và
 *   nhóm thứ hai là nhân sự nền tảng (`GET /api/v1/platform-staff`), vì người
 *   thực hiện có thể là quản trị nền tảng thao tác trên tenant.
 *
 * `tim` đã qua debounce ở component, nên khoá truy vấn không đổi theo từng phím.
 */
export function useNguoiDungLoc(tenantId: string, nenTang: boolean, tim: string) {
  const qTenant = useQuery({
    queryKey: ['nhat-ky', 'nguoi-dung', 'cong-tenant'],
    queryFn: tatCaNguoiDungTenant,
    enabled: !nenTang,
    staleTime: 5 * 60_000,
  });
  const qNenTang = useQuery({
    queryKey: ['nhat-ky', 'nguoi-dung', tenantId, tim.trim()],
    queryFn: () => nhatKyRepository.nguoiDungCuaTenant(tenantId, tim, null, MOT_LAN_TIM).then((p) => p.items),
    enabled: nenTang,
    staleTime: 60_000,
  });
  const qNhanSu = useQuery({
    queryKey: ['nhat-ky', 'nhan-su-nen-tang'],
    queryFn: () =>
      nhanSuNenTangRepository
        .danhSach({ limit: MOT_TRANG })
        .then((p) => p.items.map((s) => ({ user_id: s.id, display_name: s.ho_ten, email: s.email }))),
    enabled: nenTang,
    staleTime: 5 * 60_000,
  });

  const nguoiDung = nenTang ? qNenTang : qTenant;
  return {
    nguoiDung: nguoiDung.data ?? [],
    nhanSu: nenTang ? (qNhanSu.data ?? []) : [],
    dangTai: nguoiDung.isFetching || (nenTang && qNhanSu.isFetching),
    loi: nguoiDung.error ?? (nenTang ? qNhanSu.error : null),
  };
}
